
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader, LoadingState, EmptyState } from "@/components/features/states";
import { formatRelativeTime } from "@/lib/format";
import { useUiStore } from "@/store/ui";
import { Bell, CheckCheck, Package, MessageSquare, Banknote, ShieldCheck, Flag, Truck, Star } from "lucide-react";
import { useTranslation } from "react-i18next";

const ICONS: Record<string, React.ElementType> = {
  message: MessageSquare,
  job_assigned: Truck,
  job_picked_up: Package,
  job_delivered: Package,
  payment_confirmed: Banknote,
  payment_due: Banknote,
  trust: ShieldCheck,
  dispute: Flag,
  system: Bell,
};

export function NotificationsView() {
  const qc = useQueryClient();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications.list(),
    refetchInterval: 15000,
  });
  const notifications = data?.data?.notifications ?? [];

  const markAllRead = async () => {
    await api.notifications.markRead();
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const open = (n: any) => {
    if (!n.read) api.notifications.markRead(n.id);
    qc.invalidateQueries({ queryKey: ["notifications"] });
    try {
      const d = n.data ? JSON.parse(n.data) : {};
      if (d.jobId) navigate(`app/jobs/${d.jobId}`);
    } catch {}
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("notifications.title")}
        subtitle={t("notifications.unreadCount", { count: notifications.filter((n) => !n.read).length })}
        action={
          notifications.some((n) => !n.read) && (
            <Button variant="outline" size="sm" onClick={markAllRead}>
              <CheckCheck className="mr-1 h-4 w-4" /> {t("notifications.markAllRead")}
            </Button>
          )
        }
      />
      {isLoading ? (
        <LoadingState />
      ) : notifications.length === 0 ? (
        <EmptyState
          icon={<Bell className="h-8 w-8" />}
          title={t("notifications.noNotificationsYet")}
          description={t("notifications.noNotificationsYetDesc")}
        />
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <Card
                key={n.id}
                onClick={() => open(n)}
                className={`cursor-pointer transition hover:border-primary/40 ${!n.read ? "border-primary/30 bg-primary/5" : ""}`}
              >
                <CardContent className="flex items-start gap-3 p-3.5">
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${!n.read ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold">{n.title}</p>
                      {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                    </div>
                    <p className="line-clamp-2 text-sm text-muted-foreground">{n.body}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{formatRelativeTime(n.createdAt)}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
